import { useState, useRef, useEffect } from "react";
import type { InfluencerData } from "@/features/influencer-monitoring/store/influencerStore";
import { useInfluencerStore } from "@/features/influencer-monitoring/store/influencerStore";
import { PLATFORM_KEYS } from "@/constants/platformData";
import {
  isUrl,
  extractUsername,
  constructUrl,
} from "@/features/influencer-monitoring/utils/urlUtils";
import { normalizeLink } from "@/features/influencer-monitoring/utils/linkValidation";

type SortDirection = "asc" | "desc" | null;

interface UseInfluencerTableProps {
  data?: InfluencerData[];
  enabledPlatforms?: string[];
  isApiTokenLoading?: boolean;
  invalidLinks?: Set<string>;
  duplicateLinks?: Set<string>;
}

export function useInfluencerTable({
  data = [],
  enabledPlatforms = [],
  isApiTokenLoading = false,
  invalidLinks,
  duplicateLinks,
}: UseInfluencerTableProps) {
  const [rowCount, setRowCount] = useState(1);
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    platform: keyof InfluencerData;
  } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [sortColumn, setSortColumn] = useState<keyof InfluencerData | null>(
    null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [footerWidth, setFooterWidth] = useState("calc(100% - 10px)");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { influencerData, setInfluencerData, showLinks } = useInfluencerStore();

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [influencerData]);

  useEffect(() => {
    const checkScrollbar = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const hasVerticalScrollbar =
          container.scrollHeight > container.clientHeight;
        if (hasVerticalScrollbar) {
          const scrollbarWidth = container.offsetWidth - container.clientWidth;
          setFooterWidth(`calc(100% - ${scrollbarWidth}px)`);
        } else {
          setFooterWidth("100%");
        }
      }
    };

    checkScrollbar();

    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    const timeoutId = setTimeout(checkScrollbar, 100);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [influencerData, data]);

  const formatDisplayValue = (
    value: string,
    platform: keyof InfluencerData,
  ): string => {
    return showLinks ? value : extractUsername(value, platform);
  };

  const handleSort = (column: keyof InfluencerData) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") setSortDirection("desc");
      else if (sortDirection === "desc") {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortedData = (): InfluencerData[] => {
    const dataToSort = data.length > 0 ? data : influencerData;
    if (!sortColumn || !sortDirection) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      const aValue = a[sortColumn] || "";
      const bValue = b[sortColumn] || "";
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const handleCellClick = (
    rowIndex: number,
    platform: keyof InfluencerData,
    currentValue: string,
  ) => {
    setEditingCell({ rowIndex, platform });
    setEditValue(currentValue);
  };

  const handleInputChange = (value: string) => setEditValue(value);

  const handleSaveEdit = () => {
    if (!editingCell) {
      setEditingCell(null);
      setEditValue("");
      return;
    }

    let newData = [...influencerData];

    if (newData.length === 0) {
      newData = [
        {
          facebook: "",
          twitter: "",
          instagram: "",
          reddit: "",
          yt: "",
          tiktok: "",
        },
      ];
    }

    let valueToSave = editValue.trim();
    if (valueToSave && !isUrl(valueToSave)) {
      valueToSave = constructUrl(valueToSave, editingCell.platform);
    }

    newData[editingCell.rowIndex] = {
      ...(newData[editingCell.rowIndex] ?? {
        facebook: "",
        twitter: "",
        instagram: "",
        reddit: "",
        yt: "",
        tiktok: "",
      }),
      [editingCell.platform]: valueToSave,
    };

    setInfluencerData(newData);
    setEditingCell(null);
    setEditValue("");
  };

  const handleInputBlur = () => handleSaveEdit();

  const moveToNextCell = (isShiftKey: boolean = false) => {
    if (!editingCell) return;

    const { rowIndex, platform } = editingCell;
    const currentData = data.length > 0 ? data : influencerData;
    const platformIndex = PLATFORM_KEYS.indexOf(platform);

    let nextRowIndex = rowIndex;
    let nextPlatformIndex = platformIndex;

    if (isShiftKey) {
      nextPlatformIndex--;
      if (nextPlatformIndex < 0) {
        nextRowIndex--;
        nextPlatformIndex = PLATFORM_KEYS.length - 1;
      }
    } else {
      nextPlatformIndex++;
      if (nextPlatformIndex >= PLATFORM_KEYS.length) {
        nextRowIndex++;
        nextPlatformIndex = 0;
      }
    }

    if (nextRowIndex >= currentData.length) {
      if (!isShiftKey) {
        const newRow = {
          facebook: "",
          twitter: "",
          instagram: "",
          reddit: "",
          yt: "",
          tiktok: "",
        };
        setInfluencerData([...influencerData, newRow]);
      } else {
        return;
      }
    }

    if (nextRowIndex < 0) return;

    setTimeout(() => {
      const nextPlatform = PLATFORM_KEYS[nextPlatformIndex];
      const dataToUse =
        nextRowIndex >= currentData.length ? influencerData : currentData;
      const nextValue = dataToUse[nextRowIndex]?.[nextPlatform] || "";
      setEditingCell({ rowIndex: nextRowIndex, platform: nextPlatform });
      setEditValue(nextValue);
    }, 10);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSaveEdit();
    else if (e.key === "Escape") {
      setEditingCell(null);
      setEditValue("");
    } else if (e.key === "Tab") {
      e.preventDefault();
      handleSaveEdit();
      moveToNextCell(e.shiftKey);
    }
  };

  const handleAddRows = () => {
    const currentData = [...influencerData];
    const newRows = Array.from({ length: rowCount }, () => ({
      facebook: "",
      twitter: "",
      instagram: "",
      reddit: "",
      yt: "",
      tiktok: "",
    }));

    setInfluencerData([...currentData, ...newRows]);

    const lastNewRowIndex = currentData.length + newRows.length - 1;
    setTimeout(() => {
      setEditingCell({ rowIndex: lastNewRowIndex, platform: "facebook" });
      setEditValue("");
    }, 100);
  };

  const isPlatformDisabled = (platformKey: keyof InfluencerData): boolean => {
    if (enabledPlatforms.length === 0) return false;
    const platformMap: Record<string, string> = {
      facebook: "facebook",
      twitter: "twitter",
      instagram: "instagram",
      reddit: "reddit",
      yt: "youtube",
      tiktok: "tiktok",
    };
    const apiPlatformName = platformMap[platformKey];
    return !enabledPlatforms.includes(apiPlatformName);
  };

  const isCellInvalidOrDuplicate = (value: string): boolean => {
    if (!value || value.trim() === "") return false;
    const trimmed = value.trim();
    if (invalidLinks?.has(trimmed)) return true;
    if (duplicateLinks?.has(normalizeLink(trimmed))) return true;
    return false;
  };

  return {
    rowCount,
    setRowCount,
    editingCell,
    editValue,
    sortColumn,
    sortDirection,
    footerWidth,
    scrollContainerRef,
    isApiTokenLoading,
    getSortedData,
    formatDisplayValue,
    handleSort,
    handleCellClick,
    handleInputChange,
    handleInputKeyDown,
    handleInputBlur,
    handleAddRows,
    isPlatformDisabled,
    isCellInvalidOrDuplicate,
  };
}
